// The conversation with a contact, as a person would read it.
//
// Distinct from ContactTimeline on purpose. The timeline answers "what has
// happened to this record", one row per event, which is right for an audit
// trail and wrong for reading: a single email shows up there as six rows
// (sent, delivered, opened, opened, opened, clicked). Here one email is one
// entry with its engagement folded in, and replies sit under it in a thread.
//
// Threads are collapsed to their most recent exchange by default. The
// question you arrive with is almost always "what is the state of this
// conversation", not "what did we say in March".

import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import client from '../lib/feathers';
import EmailEditor from './EmailEditor';
import SenderPicker from './SenderPicker';

const fmt = (d) => (d ? new Date(d).toLocaleString(undefined, {
  month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
}) : '');

const SOURCE_STYLE = {
  campaign: 'bg-blue-50 text-blue-700',
  journey: 'bg-purple-50 text-purple-700',
  transactional: 'bg-gray-100 text-gray-600',
  direct: 'bg-emerald-50 text-emerald-700',
};

const PROBLEM_LABEL = {
  bounced: 'Bounced',
  spam_complaint: 'Marked as spam',
  unsubscribed: 'Unsubscribed',
};

/** Strip tags for the collapsed one-line preview. The body is rendered as
 *  real HTML only once expanded, and only inside its own bordered block. */
function preview(html, text) {
  const raw = text || String(html || '').replace(/<[^>]*>/g, ' ');
  return raw.replace(/\s+/g, ' ').trim().slice(0, 140);
}

function Engagement({ e }) {
  if (!e) return null;
  const bits = [];
  if (e.problem) {
    return (
      <span className="text-xs font-medium text-red-700">{PROBLEM_LABEL[e.problem] || e.problem}</span>
    );
  }
  if (e.openCount > 0) bits.push(`Opened${e.openCount > 1 ? ` ${e.openCount}x` : ''}`);
  else if (e.delivered) bits.push('Delivered');
  if (e.clickCount > 0) bits.push(`Clicked${e.clickCount > 1 ? ` ${e.clickCount}x` : ''}`);
  if (!bits.length) return <span className="text-xs text-gray-400">No opens yet</span>;
  return (
    <span className={`text-xs font-medium ${e.clickCount > 0 ? 'text-emerald-700' : 'text-gray-600'}`}>
      {bits.join(' · ')}
    </span>
  );
}

function Entry({ entry }) {
  const [open, setOpen] = useState(false);
  const inbound = entry.direction === 'inbound';
  const copy = entry.direction === 'outbound_copy';
  const body = entry.bodyHtml;
  const text = entry.text;
  const hasContent = !!(body || text);

  return (
    <div className={`border-l-2 pl-4 py-3 ${inbound ? 'border-emerald-400' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-900">
              {inbound ? 'They replied' : 'We sent'}
            </span>
            {copy && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600" title="Reached us as a BCC or forward of our own mail">
                copy
              </span>
            )}
            {entry.source && (
              <span className={`text-xs px-1.5 py-0.5 rounded ${SOURCE_STYLE[entry.source.type] || 'bg-gray-100 text-gray-600'}`}>
                {entry.source.label}
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mt-0.5">
            {fmt(entry.at)}
            {entry.from ? ` · ${entry.from}` : ''}
          </p>
        </div>
        <div className="shrink-0 text-right">
          {!inbound && !copy && <Engagement e={entry.engagement} />}
        </div>
      </div>

      {hasContent ? (
        <>
          {!open && (
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="mt-1.5 text-left text-sm text-gray-600 hover:text-gray-900 w-full truncate"
            >
              {preview(body, text)}
            </button>
          )}
          {open && (
            <div className="mt-2">
              {body ? (
                // Rendered in its own bordered block so a campaign's own
                // styling cannot bleed into the admin page around it.
                <div
                  className="prose prose-sm max-w-none border border-gray-200 rounded-md p-3 bg-white overflow-x-auto"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{ __html: body }}
                />
              ) : (
                <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800 border border-gray-200 rounded-md p-3 bg-white">
                  {text}
                </pre>
              )}
              <button type="button" onClick={() => setOpen(false)} className="mt-1 text-xs text-gray-500 hover:text-gray-800">
                Collapse
              </button>
            </div>
          )}
        </>
      ) : (
        // Templated sends have no body of ours: the copy lives in Postmark.
        <p className="mt-1.5 text-sm text-gray-400 italic">
          {entry.templateAlias
            ? `Sent from the "${entry.templateAlias}" template — wording lives in Postmark`
            : 'No content recorded'}
        </p>
      )}

      {entry.errorMessage && (
        <p className="mt-1 text-xs text-red-600">{entry.errorMessage}</p>
      )}
    </div>
  );
}

/** Replying from inside the thread, rather than from a modal that has
 *  forgotten what you were reading. The subject is carried over so the reply
 *  threads onto the same conversation: subject IS the thread key, so writing
 *  a fresh one silently starts a new thread. */
function ReplyBox({
  thread, contact, userId, variables, onSent, onCancel,
}) {
  const existing = thread.subject || '';
  const [subject, setSubject] = useState(
    /^\s*re\s*:/i.test(existing) ? existing : `Re: ${existing}`,
  );
  const [body, setBody] = useState('');
  const [senderIdentityId, setSenderIdentityId] = useState(null);
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      await client.service('admin/campaign-direct-send').create({
        // Exactly one of these. A user with no contact row yet is still
        // reachable: the send resolves one for them (idempotently) rather
        // than refusing, which is why the user page can reply at all.
        ...(contact ? { recipientContactId: contact.id } : { recipientUserId: userId }),
        subject: subject.trim(),
        bodyHtml: body,
        senderIdentityId: senderIdentityId || undefined,
      });
      toast.success('Reply sent');
      setBody('');
      onSent();
    } catch (err) {
      toast.error(err.message || 'Could not send the reply');
    }
    setSending(false);
  };

  return (
    <div className="py-3 border-t border-gray-200 bg-gray-50 -mx-4 px-4">
      <div className="space-y-3">
        <SenderPicker value={senderIdentityId} onChange={setSenderIdentityId} kind="personal" />
        <input
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          maxLength={200}
          aria-label="Subject"
          className="block w-full text-sm border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary"
        />
        <EmailEditor
          value={body}
          onChange={setBody}
          variables={variables || []}
          placeholder={contact ? `Reply to ${contact.email}…` : 'Write a reply…'}
          minHeight="160px"
        />
        <p className="text-xs text-gray-500">
          Goes out as a plain 1:1 email on the transactional stream. No unsubscribe footer,
          because a hand-written reply is correspondence rather than a campaign.
        </p>
        <div className="flex items-center justify-end gap-3">
          <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-200 rounded-md">
            Cancel
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || !subject.trim() || !body.trim()}
            className="px-3 py-1.5 bg-primary text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {sending ? 'Sending…' : 'Send reply'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Thread({
  thread, contact, userId, variables, onSent,
}) {
  // Long threads open to their most recent exchange; short ones open whole,
  // since collapsing two messages hides nothing worth hiding.
  const [expanded, setExpanded] = useState(thread.entries.length <= 3);
  const [replying, setReplying] = useState(false);
  const shown = expanded ? thread.entries : thread.entries.slice(-2);
  const hidden = thread.entries.length - shown.length;
  // An unsubscribed or bounced contact is refused by the send path, so
  // offering the button would be a promise we cannot keep. With no contact
  // row at all we can still write to the user, and the send creates one.
  const canReply = contact ? contact.status === 'active' : !!userId;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h4 className="text-sm font-semibold text-gray-900 truncate">{thread.subject}</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            {thread.messageCount} message{thread.messageCount === 1 ? '' : 's'} · last {fmt(thread.lastAt)}
          </p>
        </div>
        <div className="shrink-0 flex items-center gap-3">
          {thread.hasReply && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
              Replied
            </span>
          )}
          {canReply && !replying && (
            <button
              type="button"
              onClick={() => setReplying(true)}
              className="text-xs font-medium text-blue-700 hover:underline"
            >
              Reply
            </button>
          )}
        </div>
      </div>
      <div className="px-4 py-1 divide-y divide-gray-100">
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="w-full text-left py-2 text-xs text-blue-700 hover:underline"
          >
            Show {hidden} earlier message{hidden === 1 ? '' : 's'}
          </button>
        )}
        {shown.map((e) => <Entry key={e.id} entry={e} />)}
        {replying && (
          <ReplyBox
            thread={thread}
            contact={contact}
            userId={userId}
            variables={variables}
            onSent={() => { setReplying(false); onSent(); }}
            onCancel={() => setReplying(false)}
          />
        )}
      </div>
    </div>
  );
}

export default function ContactConversation({
  contactId, userId, refreshKey, variables,
}) {
  const [threads, setThreads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Keyed on what we loaded, not a bare flag: a bare flag never resets, so
  // moving between contacts would keep showing the first one's conversation.
  const loadedForRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const query = contactId ? { contactId } : { userId };
      const r = await client.service('admin/contact-conversation').find({ query });
      setThreads(r.data || []);
      setContacts(r.contacts || []);
    } catch (err) {
      setError(err.message || 'Could not load the conversation');
    }
    setLoading(false);
  }, [contactId, userId]);

  useEffect(() => {
    const key = `${contactId || ''}:${userId || ''}:${refreshKey || 0}`;
    if (!contactId && !userId) return;
    if (loadedForRef.current === key) return;
    loadedForRef.current = key;
    load();
  }, [contactId, userId, refreshKey, load]);

  if (loading) return <p className="text-sm text-gray-500">Loading conversation…</p>;
  if (error) return <p className="text-sm text-red-600">{error}</p>;
  if (!threads.length) {
    return (
      <p className="text-sm text-gray-500">
        Nothing sent or received yet. Anything emailed to this contact shows up here.
      </p>
    );
  }

  // A person can be several contact rows (a mailbox and an account are kept
  // separate on purpose), so a reply goes to the one we can actually send to.
  const replyTarget = contacts.find((c) => c.status === 'active') || contacts[0] || null;

  return (
    <div className="space-y-4">
      {threads.map((t) => (
        <Thread
          key={t.threadKey}
          thread={t}
          contact={replyTarget}
          userId={userId}
          variables={variables}
          onSent={() => { loadedForRef.current = null; load(); }}
        />
      ))}
    </div>
  );
}
